import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { FilterList, PivotTableChart, ViewColumn } from "@mui/icons-material"
import { Alert, Divider } from "@mui/material"
import Tooltip from "components/material/tooltip"
import {
  type GridInitialState,
  ColumnsPanelTrigger,
  DataGridPremium as DataGrid,
  FilterPanelTrigger,
  GridColumnMenu,
  GridColDef,
  GridPivotModel,
  PivotPanelTrigger,
  Toolbar,
  ToolbarButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium"
import { ErrorBoundary } from "@sentry/react"
import { debounce } from "lodash"
import { MAX_PIVOT_COLUMNS } from "helpers/ai"

import "./artifact-data-grid.scss"

interface DefaultToolbarContextValue {
  toolbarComponents: React.ReactNode | null
  reducedPadding: boolean
}

const DefaultToolbarContext = createContext<DefaultToolbarContextValue>({
  toolbarComponents: null,
  reducedPadding: false,
})

type ArtifactColumnMenuProps = React.ComponentProps<typeof GridColumnMenu>

function ArtifactColumnMenu(props: ArtifactColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  )
}

type DefaultToolbarProps = React.ComponentProps<typeof Toolbar>

function DefaultToolbar(props: DefaultToolbarProps) {
  const { toolbarComponents, reducedPadding } = useContext(DefaultToolbarContext)
  return (
    <Toolbar
      {...props}
      className={classNames("component_artifact-data-grid__toolbar", {
        "component_artifact-data-grid__toolbar--reduced-padding": reducedPadding,
      })}
    >
      <Tooltip title="Columns">
        <span>
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumn fontSize="small" />
          </ColumnsPanelTrigger>
        </span>
      </Tooltip>
      <Tooltip title="Filters">
        <span>
          <FilterPanelTrigger render={<ToolbarButton />}>
            <FilterList fontSize="small" />
          </FilterPanelTrigger>
        </span>
      </Tooltip>
      <Tooltip title="Pivot">
        <span>
          <PivotPanelTrigger render={<ToolbarButton />}>
            <PivotTableChart fontSize="small" />
          </PivotPanelTrigger>
        </span>
      </Tooltip>
      {toolbarComponents && (
        <>
          <Divider orientation="vertical" className="component_artifact-data-grid__divider" />
          {toolbarComponents}
        </>
      )}
    </Toolbar>
  )
}

export interface ArtifactDataGridProps {
  columns: GridColDef[]
  rows: Record<string, unknown>[]
  className?: string
  toolbarComponents?: React.ReactNode
  customToolbar?: React.ComponentType<React.ComponentProps<typeof Toolbar>>
  onPivotWarning?: (warning: string | null) => void
  reducedPadding?: boolean
  initialState?: GridInitialState
  onStateChange?: (state: GridInitialState) => void
}

export default function ArtifactDataGrid({
  columns,
  rows,
  className,
  toolbarComponents,
  customToolbar,
  onPivotWarning,
  reducedPadding = false,
  initialState,
  onStateChange,
}: ArtifactDataGridProps) {
  const apiRef = useGridApiRef()
  const [pivotWarning, setPivotWarning] = useState<string | null>(null)
  const lastReportedStateRef = useRef<string | null>(initialState != null ? JSON.stringify(initialState) : null)

  const ToolbarComponent = customToolbar || DefaultToolbar
  const defaultToolbarContextValue = useMemo(
    () => ({ toolbarComponents: toolbarComponents ?? null, reducedPadding }),
    [toolbarComponents, reducedPadding],
  )

  const onStateChangeRef = useRef(onStateChange)
  useEffect(() => {
    onStateChangeRef.current = onStateChange
  })

  const debouncedSave = useMemo(() => {
    if (!onStateChange) return null
    return debounce(() => {
      const api = apiRef.current
      const callback = onStateChangeRef.current
      if (!api || !callback) return

      const state = api.exportState()
      const newSerialized = JSON.stringify(state)

      if (newSerialized === lastReportedStateRef.current) {
        return
      }

      lastReportedStateRef.current = newSerialized
      callback(state)
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef stable; refs read in callback only
  }, [onStateChange])

  useEffect(
    () => () => {
      // Flush any pending saves to ensure DataGrid state (sorting, filters, columns) is persisted before unmount
      debouncedSave?.flush()
    },
    [debouncedSave],
  )

  const handleStateChange = useCallback(() => {
    debouncedSave?.()
  }, [debouncedSave])

  const handlePivotModelChange = useCallback(
    (model: GridPivotModel) => {
      const pivotColumns = model?.columns || []
      if (pivotColumns.length > MAX_PIVOT_COLUMNS) {
        const warning = `Too many pivot columns selected. Maximum allowed is ${MAX_PIVOT_COLUMNS}.`
        setPivotWarning(warning)
        onPivotWarning?.(warning)

        if (apiRef.current) {
          apiRef.current.setPivotModel({
            ...model,
            columns: pivotColumns.slice(0, MAX_PIVOT_COLUMNS),
          })
        }
      } else {
        setPivotWarning(null)
        onPivotWarning?.(null)
      }
    },
    [apiRef, onPivotWarning],
  )

  return (
    <>
      {pivotWarning && (
        <Alert severity="warning" className="component_artifact-data-grid__pivot-warning">
          {pivotWarning}
        </Alert>
      )}
      <ErrorBoundary fallback={<div className="grid-error-boundary">⚠️ Failed to load the grid.</div>}>
        <DefaultToolbarContext.Provider value={defaultToolbarContextValue}>
          <DataGrid
            ignoreValueFormatterDuringExport
            className={className}
            apiRef={apiRef}
            density="compact"
            rows={rows}
            columns={columns}
            initialState={initialState}
            onStateChange={handleStateChange}
            onPivotModelChange={handlePivotModelChange}
            slots={{
              columnMenu: ArtifactColumnMenu,
              toolbar: ToolbarComponent,
            }}
            showToolbar
            pagination
          />
        </DefaultToolbarContext.Provider>
      </ErrorBoundary>
    </>
  )
}
